<template>
  <LibErrorBoundary :child-props="item" :component-name="mappedComponent">
    <Suspense>
      <NinetailedExperience
        v-if="mappedComponent"
        :baseline="baselineContent"
        :component="mappedComponent"
        :experiences="mappedExperiences"
        :passthrough-props="binding ?? {}"
      />
      <LazyLibResolveError v-else-if="isEntryResolveError(item)" :item="item" />
      <div v-else-if="!isEntryNotAvailableInLanguageError(item)">
        Could not map <b>{{ contentTypeId }} {{ item?.sys?.id }}</b>
      </div>
    </Suspense>
  </LibErrorBoundary>
</template>

<script setup lang="ts">
import componentMap from './mapping';
import type { CONTENT_TYPE } from '~~/lib/ContentfulService';
import {
  type ContentfulEntryNotResolvableError,
  isEntryResolveError
} from '~/utils/guards/isEntryResolveError';
import type { ContentfulEntryNotAvailableInLanguageError } from '~/utils/guards/isEntryNotAvailableInLanguageError';
import { isEntryNotAvailableInLanguageError } from '~/utils/guards/isEntryNotAvailableInLanguageError';
import type { ExtendedEntry } from '~~/lib/Contentful/model';
import { computed } from 'vue';
import { mapBaselineContentfulEntry } from '~/utils/ninetailed/mapBaselineContentfulEntry';
import { parseExperiences } from '~/utils/ninetailed/parseExperiences';

interface Props {
  item:
    | ExtendedEntry<unknown>
    | ContentfulEntryNotResolvableError
    | ContentfulEntryNotAvailableInLanguageError;
  binding?: Record<string, any>;
}

const props = defineProps<Props>();

// Must track `props.item`: after paywall unlock the same MappedComponent
// instance receives a new page payload and must forward it to Ninetailed.
const baselineContent = computed(() => mapBaselineContentfulEntry(props.item));
const mappedExperiences = computed(() => parseExperiences(props.item));

const contentTypeId = computed(() => {
  if (
    isEntryResolveError(props.item) ||
    isEntryNotAvailableInLanguageError(props.item)
  ) {
    return;
  }
  return props.item?.sys?.contentType?.sys?.id as CONTENT_TYPE | undefined;
});

const mappedComponent = computed(() =>
  contentTypeId.value ? componentMap[contentTypeId.value] : undefined
);
</script>
